import BigNumber from 'bignumber.js';
import { distinctBigNumberUntilChanged, sharePublishReplay, swalloError } from 'ngx-libs';
import { BehaviorSubject, combineLatest, Observable, of, timer } from 'rxjs';
import { map, mergeMap, switchMap, take} from 'rxjs/operators';
import Erc20Abi from 'src/app/abi/erc20';
import { AccountService } from '../services/account.service';
import { ContractService } from '../services/contract.service';
import { EthWalletService } from '../services/eth-wallet.service';
import { Base } from './base';
import { Market } from './market';
import { PaymasterMethod } from './paymaster-method';

export class Token extends Base {
  symbol: Observable<string>;
  totalSupply: Observable<BigNumber>;
  balance: Observable<BigNumber>;
  decimalsFactor: BigNumber;

  constructor(
    public contract: string,
    public decimals: number,
    protected accountService: AccountService,
    protected ethWalletService: EthWalletService,
    protected contractService: ContractService,
    private tokenAbi: any = Erc20Abi
  ) {
    super(contract, tokenAbi, accountService, contractService);

    this.symbol = this.call((i) => i.methods.symbol()).pipe(sharePublishReplay(1));

    this.decimalsFactor = new BigNumber(10).pow(decimals);

    this.totalSupply = timer(0, 5000).pipe(
      switchMap(() => this.getTotalSupply().pipe(swalloError())),
      distinctBigNumberUntilChanged(),
      sharePublishReplay(1)
    );

    const source = combineLatest([this.accountService.account, timer(0, 5000)]).pipe(
      sharePublishReplay(1)
    );
    this.balance = source.pipe(
      switchMap(([a]) => (a ? this.balanceOf(a).pipe(swalloError()) : of(new BigNumber(0)))),
      distinctBigNumberUntilChanged(),
      sharePublishReplay(1)
    );
  }

  getTotalSupply() {
    return this.call((i) => i.methods.totalSupply()).pipe(map((v: any) => new BigNumber(v).div(this.decimalsFactor)));
  }

  mint(user: string, amount: any) {
    return this.send((i) => i.methods.mint(user, new BigNumber(amount).times(this.decimalsFactor).toString(10)));
  }

  approveAll(spender: string, paymasterMethod?: PaymasterMethod) {
    return this.approve(
      spender,
      new BigNumber('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff', 16).div(this.decimalsFactor).toString(10),
      paymasterMethod
    );
  }

  approve(spender: string, amount: any, paymasterMethod?: PaymasterMethod) {
    return this.send((i) => i.methods.approve(spender, new BigNumber(amount).times(this.decimalsFactor).toString(10)), { paymasterMethod });
  }

  allowance(user: string, spender: string) {
    return this.call((i) => i.methods.allowance(user, spender)).pipe(
      map((v: any) => new BigNumber(v).div(this.decimalsFactor))
    );
  }

  balanceOf(addr: string) {
    return this.call((i) => i.methods.balanceOf(addr)).pipe(map((v: any) => new BigNumber(v).div(this.decimalsFactor)));
  }
}
